// ─── ParentClassMedia.jsx ───────────────────────────────────────────────────
// Gallery of photos/videos the parent's class teacher(s) have shared —
// broadcasts to the whole class+section, plus anything sent individually.
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { saveAs } from 'file-saver';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PhotoIcon, ArrowDownTrayIcon, XMarkIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pcm * { box-sizing:border-box; }
  .pcm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:pcmFade .4s ease both; }
  @keyframes pcmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .pcm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .pcm-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .pcm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border:none; border-radius:9px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:14px; height:14px; stroke-width:2; }
  .btn-orange { background:#f97316; color:white; }
  .btn-orange:hover { background:#ea6c0a; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }

  .gal-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:14px; padding:16px; }
  .gal-card { border:1px solid #edf0f9; border-radius:14px; overflow:hidden; background:#fafbff; display:flex; flex-direction:column; transition:box-shadow .15s; }
  .gal-card:hover { box-shadow:0 6px 20px rgba(15,17,35,.08); }
  .gal-media { width:100%; height:170px; object-fit:cover; background:#e8ebf4; display:block; cursor:zoom-in; }
  .gal-video { width:100%; height:170px; background:#000; display:block; }
  .gal-body { padding:11px 13px; display:flex; flex-direction:column; gap:6px; flex:1; }
  .gal-teacher { display:flex; align-items:center; gap:6px; font-size:.74rem; font-weight:700; color:#f97316; }
  .gal-teacher svg { width:14px; height:14px; stroke-width:2; }
  .gal-caption { font-size:.78rem; color:#1a1d2e; line-height:1.4; }
  .gal-meta { font-size:.68rem; color:#9094a8; display:flex; flex-direction:column; gap:2px; }
  .gal-foot { display:flex; gap:8px; margin-top:auto; padding-top:6px; }
  .gal-foot .btn { flex:1; justify-content:center; }

  .pcm-empty { text-align:center; padding:3.5rem 2rem; }
  .pcm-empty-icon { width:48px; height:48px; border-radius:13px; background:#fff7ed; color:#f97316; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .pcm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .pcm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .pcm-empty-sub { font-size:.76rem; color:#9094a8; }

  .pcm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .lightbox-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.85); display:flex; align-items:center; justify-content:center; z-index:100000; padding:20px; }
  .lightbox-box { max-width:90vw; max-height:90vh; display:flex; flex-direction:column; align-items:center; gap:12px; }
  .lightbox-box img, .lightbox-box video { max-width:90vw; max-height:76vh; border-radius:10px; box-shadow:0 20px 60px rgba(0,0,0,.4); }
  .lightbox-close { position:absolute; top:18px; right:18px; width:34px; height:34px; border-radius:9px; background:rgba(255,255,255,.12); border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .lightbox-close svg { width:16px; height:16px; stroke:white; stroke-width:2; }
  .lightbox-caption { color:#e4e7f0; font-size:.82rem; text-align:center; max-width:70vw; }

  @media (max-width:768px) {
    .pcm-title { font-size:1.1rem; }
  }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const filenameFromUrl = (url, fallback) => {
  try {
    const path = new URL(url).pathname;
    const name = decodeURIComponent(path.split('/').pop() || '');
    return name || fallback;
  } catch {
    return fallback;
  }
};

// Cross-origin (Backblaze B2) URLs won't honour a plain <a download> in most
// browsers — they just navigate. Fetch the file as a blob and hand it to
// file-saver so it reliably triggers a "Save As" instead.
const downloadFile = async (url, filename) => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('download failed');
    const blob = await res.blob();
    saveAs(blob, filename);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.error('Could not download directly — opened in a new tab instead');
  }
};

const ParentClassMedia = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // the media item being viewed full-size

  useEffect(() => {
    api.get('/parent/class-media')
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error('Failed to load class media'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = (m) => {
    downloadFile(m.fileUrl, filenameFromUrl(m.fileUrl, `${m.caption || 'class-media'}.${m.mediaType === 'video' ? 'mp4' : 'jpg'}`));
  };

  return (
    <div className="pcm"><style>{style}</style>
      <div className="pcm-header">
        <div><div className="pcm-title">Class Gallery</div><div className="pcm-sub">Photos and videos shared by your child's class teacher</div></div>
      </div>

      <div className="panel">
        <div className="panel-head"><div className="panel-title">Shared With You ({items.length})</div></div>
        {loading ? (
          <div className="pcm-loading"><div className="spinner" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="pcm-empty">
            <div className="pcm-empty-icon"><PhotoIcon /></div>
            <div className="pcm-empty-title">Nothing shared yet</div>
            <div className="pcm-empty-sub">Photos and videos your class teacher shares will show up here.</div>
          </div>
        ) : (
          <div className="gal-grid">
            {items.map(m => (
              <div key={m._id} className="gal-card">
                {m.mediaType === 'image' ? (
                  <img className="gal-media" src={m.fileUrl} alt={m.caption || 'shared media'} onClick={() => setLightbox(m)} />
                ) : (
                  <video className="gal-video" src={m.fileUrl} controls preload="metadata" />
                )}
                <div className="gal-body">
                  <div className="gal-teacher"><UserCircleIcon />{m.teacherName || 'Class Teacher'}</div>
                  {m.caption && <div className="gal-caption">{m.caption}</div>}
                  <div className="gal-meta">
                    <span>{m.className}{m.section ? ` · Section ${m.section}` : ''}</span>
                    <span>{fmtDate(m.createdAt)}</span>
                  </div>
                  <div className="gal-foot">
                    {m.mediaType === 'image' && (
                      <button className="btn btn-ghost" onClick={() => setLightbox(m)}><PhotoIcon />View</button>
                    )}
                    <button className="btn btn-orange" onClick={() => handleDownload(m)}><ArrowDownTrayIcon />Download</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightbox && createPortal(
        <div className="lightbox-backdrop" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}><XMarkIcon /></button>
          <div className="lightbox-box" onClick={e => e.stopPropagation()}>
            {lightbox.mediaType === 'image'
              ? <img src={lightbox.fileUrl} alt={lightbox.caption || 'shared media'} />
              : <video src={lightbox.fileUrl} controls autoPlay />}
            {lightbox.caption && <div className="lightbox-caption">{lightbox.caption}</div>}
            <button className="btn btn-orange" onClick={() => handleDownload(lightbox)}><ArrowDownTrayIcon />Download</button>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default ParentClassMedia;
